import { memo, useMemo, type CSSProperties } from 'react'

import { cn } from '@/lib/utils'

import { dayStatusOf, hasWorker, SHORT_REASON, type CheckResult, type ScheduleWorker } from '../utils/eligibility'
import type { ScheduleJobRow } from '../utils/job-rows'
import type { ScheduleBoard } from '../utils/schedule-board'
import type { ScheduleSelection } from '../utils/selection'
import { CompletedIcon, WarningIcon, WorkerAvatar } from './ScheduleBits'
import { BRAND, categoryTone, COMPLETED_TONE, describeDay, formatJobRef, LEAVE_HATCH, OFF_HATCH, plural, PROBLEM_RED } from './schedule-styles'

const GRID_TEMPLATE = '160px repeat(7, minmax(0, 1fr))'

/** Start of `timeLabel` ("HH:mm"): the same clock as the start minute, or tenant time when the board has a timezone. */
export const jobTimeLabel = (job: ScheduleJobRow) => (job.startMinute == null ? '--:--' : job.timeLabel.split(' - ')[0])

/** "#10412345 · Basic · 09:00 - 10:00 · Property Management · Completed" (tooltip). */
export const jobTip = (job: ScheduleJobRow) => [
  formatJobRef(job.jobRef),
  job.title,
  job.timeLabel,
  job.categoryLabel || 'No category',
  job.isCompleted ? 'Completed' : null,
].filter(Boolean).join(' · ')

/** Accessible name of a chip / timeline block. */
export const jobAriaLabel = (job: ScheduleJobRow, problemText: string | null) => {
  if (job.isCompleted) return `${job.title}, ${job.timeLabel}, completed. Show job details`
  return `${job.title}, ${job.timeLabel}${problemText ? `, problem: ${problemText}` : ''}. Find a worker`
}

/** Workers are marked "Can take" / dimmed only for a selected job that can still be staffed. */
export const staffableJob = (job: ScheduleJobRow | null) => (job && !job.isCompleted ? job : null)

/** Row label under the worker's name: the selected job's verdict, else their week load. */
export function workerVerdict(worker: ScheduleWorker, selectedJob: ScheduleJobRow | null, verdict: CheckResult | null, fallback: string) {
  if (!selectedJob || !verdict) return { text: fallback, tone: 'muted' as const, dim: false }
  if (verdict.ok) return { text: hasWorker(selectedJob, worker.id) ? 'Assigned' : 'Can take', tone: 'green' as const, dim: false }
  return { text: SHORT_REASON[verdict.kind as keyof typeof SHORT_REASON], tone: 'red' as const, dim: true }
}

export const VERDICT_CLASS = {
  muted: 'text-[#4A5A51]',
  green: 'font-bold text-[#14532D]',
  red: 'font-semibold text-[#8A1C12]',
}

function JobChip({ job, problem, selected, onPick }: { job: ScheduleJobRow; problem: CheckResult | null; selected: boolean; onPick: (job: ScheduleJobRow) => void }) {
  const tone = job.isCompleted ? COMPLETED_TONE : categoryTone(job.categoryLabel)
  return (
    <button
      type="button"
      onClick={() => onPick(job)}
      title={problem ? `${jobTip(job)} · Problem: ${problem.text}` : jobTip(job)}
      aria-label={jobAriaLabel(job, problem?.text ?? null)}
      aria-pressed={selected}
      className="flex min-h-7 w-full min-w-0 items-center gap-1 rounded-lg px-1.5 py-[3px] text-left text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B] focus-visible:ring-offset-1"
      style={{
        background: tone.bg,
        color: tone.fg,
        border: `2px solid ${problem ? PROBLEM_RED : 'transparent'}`,
        boxShadow: selected ? '0 0 0 2px #14231B' : undefined,
      }}
    >
      {job.isCompleted ? <CompletedIcon /> : problem ? <WarningIcon /> : null}
      <span className="flex-none tabular-nums">{jobTimeLabel(job)}</span>
      <span className="truncate">{job.title}</span>
    </button>
  )
}

const WeekRow = memo(function WeekRow({
  worker,
  board,
  jobsByDay,
  selectedJob,
  isSelectedWorker,
  onPickJob,
  onPickWorker,
}: {
  worker: ScheduleWorker
  board: ScheduleBoard
  jobsByDay: Map<string, ScheduleJobRow[]>
  selectedJob: ScheduleJobRow | null
  isSelectedWorker: boolean
  onPickJob: (job: ScheduleJobRow) => void
  onPickWorker: (worker: ScheduleWorker) => void
}) {
  const total = board.days.reduce((count, day) => count + (jobsByDay.get(day)?.length ?? 0), 0)
  const verdictJob = staffableJob(selectedJob)
  const selectedVerdict = verdictJob ? board.check(worker, verdictJob) : null
  const label = workerVerdict(worker, verdictJob, selectedVerdict, total ? `${plural(total, 'job')} this week` : 'No jobs this week')

  return (
    <div className="grid border-b border-[#E3EAE2]" style={{ gridTemplateColumns: GRID_TEMPLATE }}>
      <button
        type="button"
        onClick={() => onPickWorker(worker)}
        aria-label={`Show open jobs for ${worker.name}`}
        aria-pressed={isSelectedWorker}
        className={cn(
          'flex min-h-11 items-center gap-2.5 px-3.5 py-2.5 text-left text-[#14231B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#14231B]',
          isSelectedWorker ? 'bg-[#E7F5EC]' : 'bg-white hover:bg-[#F4F9F5]',
        )}
      >
        <WorkerAvatar name={worker.name} dim={label.dim} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{worker.name}</span>
          <span className={cn('block text-xs', VERDICT_CLASS[label.tone])}>{label.text}</span>
        </span>
      </button>

      {board.days.map((day) => {
        const status = dayStatusOf(worker, day)
        const jobs = jobsByDay.get(day) ?? []
        let style: CSSProperties = { background: '#F4F9F5' }
        let top = status.kind === 'shift' ? status.label : 'Hours not set'
        let topClass = 'font-medium text-[#4A5A51]'
        if (status.kind === 'leave') {
          style = LEAVE_HATCH
          top = 'On leave'
          topClass = 'font-bold text-[#42237A]'
        } else if (status.kind === 'off') {
          style = OFF_HATCH
          top = 'Day off'
          topClass = 'font-bold text-[#2F3E36]'
        }

        let hint: { text: string; className: string } | null = null
        if (verdictJob && verdictJob.date === day && selectedVerdict) {
          if (selectedVerdict.ok) {
            style = { background: '#E3F3E8', boxShadow: `inset 0 0 0 2px ${BRAND}` }
            hint = { text: hasWorker(verdictJob, worker.id) ? 'Assigned' : 'Can take', className: 'text-[#14532D]' }
          } else {
            style = { ...style, opacity: 0.55 }
            if (status.kind === 'shift' || status.kind === 'unset') {
              hint = { text: SHORT_REASON[selectedVerdict.kind as keyof typeof SHORT_REASON], className: 'text-[#8A1C12]' }
            }
          }
        }

        return (
          <div key={day} className="flex min-h-16 min-w-0 flex-col gap-1 border-l border-[#E3EAE2] p-1.5" style={style}>
            <div className={cn('text-xs', topClass)}>{top}</div>
            {hint ? <div className={cn('text-xs font-bold', hint.className)}>{hint.text}</div> : null}
            {jobs.map((job) => {
              const verdict = board.check(worker, job)
              return (
                <JobChip
                  key={job.key}
                  job={job}
                  problem={verdict.ok ? null : verdict}
                  selected={selectedJob?.key === job.key}
                  onPick={onPickJob}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
})

/** Workers x 7 days: availability, each worker's jobs, and the selected job's verdict per worker. */
export function WeekBoard({
  board,
  selection,
  selectedDay,
  today,
  onPickJob,
  onPickWorker,
  onOpenDay,
}: {
  board: ScheduleBoard
  selection: ScheduleSelection
  selectedDay: string
  today: string
  onPickJob: (job: ScheduleJobRow) => void
  onPickWorker: (worker: ScheduleWorker) => void
  onOpenDay: (day: string) => void
}) {
  const selectedJob = selection?.type === 'job' ? board.jobByKey.get(selection.key) ?? null : null

  // Shown jobs per worker per day.
  const jobsByWorker = useMemo(() => {
    const map = new Map<string, Map<string, ScheduleJobRow[]>>()
    board.jobs.forEach((job) => {
      job.workers.forEach(({ workerId }) => {
        const byDay = map.get(workerId) ?? new Map<string, ScheduleJobRow[]>()
        byDay.set(job.date, [...(byDay.get(job.date) ?? []), job])
        map.set(workerId, byDay)
      })
    })
    return map
  }, [board.jobs])

  const dayCounts = useMemo(() => new Map(board.days.map((day) => [
    day,
    {
      jobs: board.jobs.filter((job) => job.date === day).length,
      toFix: board.attention.filter((item) => item.job.date === day).length,
    },
  ])), [board.attention, board.days, board.jobs])

  const empty = useMemo(() => new Map<string, ScheduleJobRow[]>(), [])

  return (
    <div className="min-w-[780px]">
      <div className="grid border-y border-[#E6ECE5]" style={{ gridTemplateColumns: GRID_TEMPLATE }}>
        <div className="flex items-end px-3.5 pb-2.5 text-xs font-bold uppercase tracking-[0.6px] text-[#4A5A51]">Worker</div>
        {board.days.map((day) => {
          const info = describeDay(day)
          const counts = dayCounts.get(day) ?? { jobs: 0, toFix: 0 }
          const countText = counts.jobs ? plural(counts.jobs, 'job') : 'No jobs'
          return (
            <button
              key={day}
              type="button"
              onClick={() => onOpenDay(day)}
              aria-label={`Open ${info.long} in day view${counts.toFix ? `, ${counts.toFix} to fix` : ''}`}
              className={cn(
                'block min-h-11 border-l border-[#E3EAE2] px-2.5 pb-2 pt-2.5 text-left text-[#14231B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#14231B]',
                day === selectedDay ? 'bg-[#E7F5EC]' : 'hover:bg-[#F4F9F5]',
              )}
            >
              <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#3C4B43]">
                {info.dow}
                {day === today ? <span className="rounded-full bg-[#14231B] px-[7px] py-px text-xs font-bold text-white">Today</span> : null}
              </span>
              <span className="block text-[22px] font-bold leading-tight">{info.num}</span>
              <span className={cn('block text-xs', counts.toFix ? 'font-bold text-[#8A1C12]' : 'font-medium text-[#4A5A51]')}>
                {counts.toFix ? `${countText} · ${counts.toFix} to fix` : countText}
              </span>
            </button>
          )
        })}
      </div>

      {board.workers.length === 0 ? (
        <p className="px-5 py-6 text-sm text-[#4A5A51]">No workers have jobs, availability or leave in this week.</p>
      ) : (
        board.workers.map((worker) => (
          <WeekRow
            key={worker.id}
            worker={worker}
            board={board}
            jobsByDay={jobsByWorker.get(worker.id) ?? empty}
            selectedJob={selectedJob}
            isSelectedWorker={selection?.type === 'worker' && selection.id === worker.id}
            onPickJob={onPickJob}
            onPickWorker={onPickWorker}
          />
        ))
      )}
    </div>
  )
}
